import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import SignatureCanvas from "react-signature-canvas";
import { createReceiving, deleteReceiving, fetchReceivings, updateReceiving, updateReceivingInvoice, type ReceivingFilters } from "@/lib/adminReceivingsApi";
import { fetchSuppliers } from "@/lib/adminSuppliersApi";
import { fetchAdminProducts } from "@/lib/adminProductsApi";
import { hasPermission, type AdminProductSummary, type Receiving, type Supplier } from "@/lib/types";
import { useApp } from "@/store/useApp";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DatePicker } from "@/components/ui/date-picker";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  ClipboardList,
  Download,
  Eraser,
  Loader2,
  Pencil,
  Plus,
  Printer,
  Receipt,
  Search,
  Trash2,
} from "lucide-react";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";

function formatDate(iso: string) {
  return new Date(iso).toLocaleString("pt-BR", {
    day: "2-digit", month: "2-digit", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
}

function formatQty(n: number) {
  return n % 1 === 0 ? n.toFixed(0) : n.toFixed(3).replace(/\.?0+$/, "");
}

function summarizeItems(receiving: Receiving) {
  const [firstItem, ...rest] = receiving.items;
  if (!firstItem) return "Sem itens";
  if (rest.length === 0) {
    return `${firstItem.productName} ${formatQty(firstItem.quantity)} ${firstItem.unit}`;
  }
  return `${firstItem.productName} ${formatQty(firstItem.quantity)} ${firstItem.unit} + ${rest.length} ${rest.length > 1 ? "itens" : "item"}`;
}

interface ReceivingItemDraft {
  productId: string;
  unit: string;
  quantity: string;
}

interface ReceivingDraft {
  supplierId: string;
  receivedAt: string;
  invoiceNumber: string;
  deliveryPersonName: string;
  deliveryPersonDocument: string;
  collectSignature: boolean;
  items: ReceivingItemDraft[];
}

function nowLocalIso() {
  const now = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}T${pad(now.getHours())}:${pad(now.getMinutes())}`;
}

function toLocalInput(iso: string) {
  const date = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

const EMPTY_ITEM = (): ReceivingItemDraft => ({ productId: "", unit: "", quantity: "" });

const EMPTY_DRAFT = (): ReceivingDraft => ({
  supplierId: "",
  receivedAt: nowLocalIso(),
  invoiceNumber: "",
  deliveryPersonName: "",
  deliveryPersonDocument: "",
  collectSignature: false,
  items: [EMPTY_ITEM()],
});

const PAGE_SIZE = 10;
const ALL_VALUE = "__all__";

export default function AdminReceivings() {
  const [receivings, setReceivings] = useState<Receiving[]>([]);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [products, setProducts] = useState<AdminProductSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [supplierFilter, setSupplierFilter] = useState<string>("");
  const [productFilter, setProductFilter] = useState<string>("");
  const [fromFilter, setFromFilter] = useState<string>("");
  const [toFilter, setToFilter] = useState<string>("");
  const [currentPage, setCurrentPage] = useState(1);
  const [formOpen, setFormOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Receiving | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [selectedReceiving, setSelectedReceiving] = useState<Receiving | null>(null);
  const [draft, setDraft] = useState<ReceivingDraft>(EMPTY_DRAFT());
  // Cada ação de recebimento é uma permissão separada (o backend revalida).
  const account = useApp((state) => state.account);
  const canCreate = hasPermission(account, "SUPRIMENTOS.RECEBIMENTO.CRIAR");
  const canEdit = hasPermission(account, "SUPRIMENTOS.RECEBIMENTO.EDITAR");
  const canDelete = hasPermission(account, "SUPRIMENTOS.RECEBIMENTO.EXCLUIR");
  const canManage = canEdit || canDelete;
  // "all" = todos; "missing" = só sem NF; "present" = só com NF.
  const [invoiceFilter, setInvoiceFilter] = useState<"all" | "missing" | "present">("all");
  const [invoiceModalOpen, setInvoiceModalOpen] = useState(false);
  const [invoiceDraft, setInvoiceDraft] = useState<{ number: string; reason: string }>({ number: "", reason: "" });
  const [savingInvoice, setSavingInvoice] = useState(false);
  const [saving, setSaving] = useState(false);
  const signaturePadRef = useRef<SignatureCanvas | null>(null);

  const filterPayload = useCallback((): ReceivingFilters => {
    const filters: ReceivingFilters = {};
    if (supplierFilter) filters.supplierId = supplierFilter;
    if (productFilter) filters.productId = productFilter;
    if (fromFilter) filters.from = fromFilter;
    if (toFilter) filters.to = toFilter;
    return filters;
  }, [supplierFilter, productFilter, fromFilter, toFilter]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [receivingsResponse, suppliersResponse, productsResponse] = await Promise.all([
        fetchReceivings(filterPayload()),
        fetchSuppliers(),
        fetchAdminProducts(),
      ]);
      setReceivings(receivingsResponse);
      setSuppliers(suppliersResponse.filter(supplier => supplier.active));
      setProducts(productsResponse.filter(product => !product.isProcessed && product.active));
    } catch {
      toast.error("Erro ao carregar dados");
    } finally {
      setLoading(false);
    }
  }, [filterPayload]);

  useEffect(() => { void load(); }, [load]);
  useEffect(() => { setCurrentPage(1); }, [search, supplierFilter, productFilter, fromFilter, toFilter, invoiceFilter]);

  const openForm = () => {
    setEditingId(null);
    setDraft(EMPTY_DRAFT());
    setFormOpen(true);
  };

  const openEdit = (receiving: Receiving) => {
    setEditingId(receiving.id);
    setDraft({
      supplierId: receiving.supplierId,
      receivedAt: toLocalInput(receiving.receivedAt),
      invoiceNumber: receiving.invoiceNumber ?? "",
      deliveryPersonName: receiving.deliveryPersonName ?? "",
      deliveryPersonDocument: receiving.deliveryPersonDocument ?? "",
      collectSignature: false,
      items: receiving.items.length > 0
        ? receiving.items.map(item => ({
            productId: item.productId,
            unit: item.unit,
            quantity: String(item.quantity),
          }))
        : [EMPTY_ITEM()],
    });
    setFormOpen(true);
  };

  const closeForm = () => {
    setFormOpen(false);
    setEditingId(null);
    setDraft(EMPTY_DRAFT());
    signaturePadRef.current?.clear();
  };

  const itemUnits = (productId: string) => {
    const product = products.find(entry => entry.id === productId);
    if (!product) return [];
    return [product.defaultUnit, ...product.availableUnits.filter(unit => unit !== product.defaultUnit)];
  };

  const updateItem = (index: number, next: Partial<ReceivingItemDraft>) => {
    setDraft(current => ({
      ...current,
      items: current.items.map((item, itemIndex) => {
        if (itemIndex !== index) return item;
        const updated = { ...item, ...next };
        if (next.productId !== undefined) {
          const units = itemUnits(next.productId);
          const unit = units.includes(updated.unit) ? updated.unit : (units[0] ?? "");
          return { ...updated, unit };
        }
        return updated;
      }),
    }));
  };

  const addItem = () => {
    setDraft(current => ({ ...current, items: [EMPTY_ITEM(), ...current.items] }));
  };

  const removeItem = (index: number) => {
    setDraft(current => ({
      ...current,
      items: current.items.length === 1
        ? current.items
        : current.items.filter((_, itemIndex) => itemIndex !== index),
    }));
  };

  const isEditing = editingId !== null;
  const hasInvoice = draft.invoiceNumber.trim().length > 0;
  // Na edição não mexemos em assinatura/comprovante já capturados — o bloco fica oculto.
  const signatureRequired = !isEditing && !hasInvoice;
  const showSignatureBlock = !isEditing && (signatureRequired || draft.collectSignature);

  const clearSignature = () => signaturePadRef.current?.clear();

  const handleSave = async () => {
    if (!draft.supplierId) { toast.error("Selecione o fornecedor"); return; }
    if (!draft.receivedAt) { toast.error("Data/hora é obrigatória"); return; }

    const items = [];
    for (const [index, item] of draft.items.entries()) {
      if (!item.productId) { toast.error(`Selecione o produto do item ${index + 1}`); return; }
      if (!item.unit) { toast.error(`Selecione a unidade do item ${index + 1}`); return; }
      const quantity = parseFloat(item.quantity);
      if (!item.quantity || Number.isNaN(quantity) || quantity <= 0) {
        toast.error(`Quantidade inválida no item ${index + 1}`);
        return;
      }
      items.push({
        productId: item.productId,
        unit: item.unit,
        quantity,
      });
    }

    if (isEditing && editingId) {
      setSaving(true);
      try {
        const updated = await updateReceiving(editingId, {
          supplierId: draft.supplierId,
          receivedAt: new Date(draft.receivedAt).toISOString(),
          invoiceNumber: draft.invoiceNumber.trim() || undefined,
          items,
        });
        setReceivings(prev => prev.map(r => r.id === updated.id ? updated : r));
        if (selectedReceiving?.id === updated.id) setSelectedReceiving(updated);
        toast.success("Recebimento atualizado");
        closeForm();
      } catch (err) {
        toast.error(err instanceof Error ? err.message : "Erro ao atualizar recebimento");
      } finally {
        setSaving(false);
      }
      return;
    }

    let signatureBase64: string | undefined;
    if (showSignatureBlock) {
      const pad = signaturePadRef.current;
      if (signatureRequired) {
        if (!draft.deliveryPersonName.trim()) { toast.error("Nome do entregador é obrigatório"); return; }
        if (!draft.deliveryPersonDocument.trim()) { toast.error("Documento do entregador é obrigatório"); return; }
        if (!pad || pad.isEmpty()) { toast.error("Assinatura do entregador é obrigatória"); return; }
      }
      if (pad && !pad.isEmpty()) {
        signatureBase64 = pad.getCanvas().toDataURL("image/png");
      }
    }

    setSaving(true);
    try {
      const created = await createReceiving({
        supplierId: draft.supplierId,
        receivedAt: new Date(draft.receivedAt).toISOString(),
        invoiceNumber: draft.invoiceNumber.trim() || undefined,
        deliveryPersonName: draft.deliveryPersonName.trim() || undefined,
        deliveryPersonDocument: draft.deliveryPersonDocument.trim() || undefined,
        signatureBase64,
        items,
      });
      setReceivings(prev => [created, ...prev]);
      setCurrentPage(1);
      toast.success("Recebimento registrado");
      closeForm();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Erro ao registrar recebimento");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteReceiving(deleteTarget.id);
      setReceivings(prev => prev.filter(r => r.id !== deleteTarget.id));
      if (selectedReceiving?.id === deleteTarget.id) setSelectedReceiving(null);
      toast.success("Recebimento excluído");
      setDeleteTarget(null);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Erro ao excluir recebimento");
    } finally {
      setDeleting(false);
    }
  };

  const filtered = receivings.filter(receiving => {
    const query = search.toLowerCase();
    const matchesSearch = receiving.supplierName.toLowerCase().includes(query)
      || (receiving.invoiceNumber ?? "").toLowerCase().includes(query)
      || receiving.items.some(item => item.productName.toLowerCase().includes(query));
    if (!matchesSearch) return false;
    const hasInvoice = (receiving.invoiceNumber ?? "").trim().length > 0;
    if (invoiceFilter === "missing" && hasInvoice) return false;
    if (invoiceFilter === "present" && !hasInvoice) return false;
    return true;
  });

  const missingInvoiceCount = receivings.filter(r => !(r.invoiceNumber ?? "").trim()).length;

  const openInvoiceModal = () => {
    if (!selectedReceiving) return;
    setInvoiceDraft({ number: selectedReceiving.invoiceNumber ?? "", reason: "" });
    setInvoiceModalOpen(true);
  };

  const submitInvoice = async () => {
    if (!selectedReceiving) return;
    const number = invoiceDraft.number.trim();
    if (!number) {
      toast.error("Informe o numero da NF");
      return;
    }
    const previous = (selectedReceiving.invoiceNumber ?? "").trim();
    const isReplacement = previous.length > 0 && previous !== number;
    const reason = invoiceDraft.reason.trim();
    if (isReplacement && !reason) {
      toast.error("Justificativa e obrigatoria para alterar uma NF existente");
      return;
    }
    setSavingInvoice(true);
    try {
      const updated = await updateReceivingInvoice(selectedReceiving.id, {
        invoiceNumber: number,
        reason: reason || undefined,
      });
      setReceivings(current => current.map(r => r.id === updated.id ? updated : r));
      setSelectedReceiving(updated);
      setInvoiceModalOpen(false);
      toast.success(previous ? "NF atualizada" : "NF anexada");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Erro ao salvar NF");
    } finally {
      setSavingInvoice(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safeCurrentPage = Math.min(currentPage, totalPages);
  const paginatedReceivings = useMemo(() => {
    const start = (safeCurrentPage - 1) * PAGE_SIZE;
    return filtered.slice(start, start + PAGE_SIZE);
  }, [filtered, safeCurrentPage]);

  const pageNumbers = useMemo(() => {
    const pages = new Set<number>([1, totalPages, safeCurrentPage - 1, safeCurrentPage, safeCurrentPage + 1]);
    return Array.from(pages)
      .filter(page => page >= 1 && page <= totalPages)
      .sort((a, b) => a - b);
  }, [safeCurrentPage, totalPages]);

  const printReceipt = (url: string) => {
    const win = window.open(url, "_blank");
    if (!win) {
      toast.error("Permita pop-ups para imprimir");
      return;
    }
    win.addEventListener("load", () => {
      win.focus();
      win.print();
    });
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="rounded-2xl bg-gradient-to-br from-primary to-primary/80 p-6 text-primary-foreground">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold">Recebimentos</h1>
            <p className="mt-1 text-sm text-primary-foreground/80">
              Registre entradas de produtos no estoque.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {canCreate && (
              <Button variant="secondary" onClick={openForm} className="gap-2">
                <Plus className="h-4 w-4" />
                Novo recebimento
              </Button>
            )}
          </div>
        </div>

        <div className="mt-4 grid gap-3 md:grid-cols-[2fr_1fr_1fr_1fr_1fr_1fr]">
          <div className="flex items-center gap-2 rounded-lg bg-white/10 px-3 py-2">
            <Search className="h-4 w-4 text-primary-foreground/60" />
            <input
              className="flex-1 bg-transparent text-sm text-primary-foreground placeholder:text-primary-foreground/50 outline-none"
              placeholder="Buscar por produto, fornecedor ou NF"
              value={search}
              onChange={event => setSearch(event.target.value)}
            />
          </div>
          <DatePicker
            className="w-40 bg-white/10 border-white/20 text-primary-foreground hover:bg-white/20 hover:text-primary-foreground"
            value={fromFilter}
            toDate={toFilter || undefined}
            placeholder="De"
            onChange={next => {
              setFromFilter(next);
              if (next && toFilter && next > toFilter) setToFilter(next);
            }}
          />
          <DatePicker
            className="w-40 bg-white/10 border-white/20 text-primary-foreground hover:bg-white/20 hover:text-primary-foreground"
            value={toFilter}
            fromDate={fromFilter || undefined}
            placeholder="Até"
            onChange={next => {
              setToFilter(next);
              if (next && fromFilter && next < fromFilter) setFromFilter(next);
            }}
          />
          <Select
            value={supplierFilter || ALL_VALUE}
            onValueChange={value => setSupplierFilter(value === ALL_VALUE ? "" : value)}
          >
            <SelectTrigger className="bg-white/10 text-primary-foreground">
              <SelectValue placeholder="Fornecedor" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL_VALUE}>Todos os fornecedores</SelectItem>
              {suppliers.map(supplier => (
                <SelectItem key={supplier.id} value={supplier.id}>{supplier.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select
            value={productFilter || ALL_VALUE}
            onValueChange={value => setProductFilter(value === ALL_VALUE ? "" : value)}
          >
            <SelectTrigger className="bg-white/10 text-primary-foreground">
              <SelectValue placeholder="Produto" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL_VALUE}>Todos os produtos</SelectItem>
              {products.map(product => (
                <SelectItem key={product.id} value={product.id}>{product.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select
            value={invoiceFilter}
            onValueChange={value => setInvoiceFilter(value as "all" | "missing" | "present")}
          >
            <SelectTrigger className="bg-white/10 text-primary-foreground">
              <SelectValue placeholder="NF" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">
                NF: todas {missingInvoiceCount > 0 ? `(${missingInvoiceCount} sem NF)` : ""}
              </SelectItem>
              <SelectItem value="missing">Apenas sem NF</SelectItem>
              <SelectItem value="present">Apenas com NF</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center gap-3 py-16 text-muted-foreground">
          <ClipboardList className="h-10 w-10 opacity-30" />
          <p className="text-sm">{search ? "Nenhum recebimento encontrado" : "Nenhum recebimento registrado"}</p>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="rounded-xl border bg-card shadow-sm">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Itens</TableHead>
                  <TableHead>Fornecedor</TableHead>
                  <TableHead>NF</TableHead>
                  <TableHead>Data</TableHead>
                  <TableHead>Registrado por</TableHead>
                  {canManage && <TableHead className="text-right">Ações</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {paginatedReceivings.map(receiving => (
                  <TableRow
                    key={receiving.id}
                    className="cursor-pointer"
                    onClick={() => setSelectedReceiving(receiving)}
                  >
                    <TableCell>
                      <div className="flex flex-col gap-1">
                        <span className="font-medium">{summarizeItems(receiving)}</span>
                        <span className="text-xs text-muted-foreground">{receiving.items.length} {receiving.items.length > 1 ? "itens" : "item"}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-sm">{receiving.supplierName}</TableCell>
                    <TableCell>
                      {receiving.invoiceNumber ? (
                        <Badge variant="outline" className="text-xs font-normal">{receiving.invoiceNumber}</Badge>
                      ) : (
                        <Badge variant="outline" className="border-amber-300 bg-amber-50 text-xs font-normal text-amber-700">Sem NF</Badge>
                      )}
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-xs text-muted-foreground">
                      {formatDate(receiving.receivedAt)}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {receiving.registeredByName || receiving.registeredByEmail}
                    </TableCell>
                    {canManage && (
                      <TableCell className="text-right" onClick={event => event.stopPropagation()}>
                        <div className="flex justify-end gap-1">
                          {canEdit && (
                            <Button
                              variant="ghost"
                              size="icon"
                              title="Editar recebimento"
                              onClick={() => openEdit(receiving)}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                          )}
                          {canDelete && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="text-destructive hover:text-destructive"
                              title="Excluir recebimento"
                              onClick={() => setDeleteTarget(receiving)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="text-sm text-muted-foreground">
              Mostrando {(safeCurrentPage - 1) * PAGE_SIZE + 1}-{Math.min(safeCurrentPage * PAGE_SIZE, filtered.length)} de {filtered.length}
            </div>
            <Pagination className="mx-0 w-auto justify-end">
              <PaginationContent>
                <PaginationItem>
                  <PaginationPrevious
                    href="#"
                    onClick={event => {
                      event.preventDefault();
                      if (safeCurrentPage > 1) setCurrentPage(safeCurrentPage - 1);
                    }}
                    className={safeCurrentPage === 1 ? "pointer-events-none opacity-50" : ""}
                  />
                </PaginationItem>
                {pageNumbers.map(page => (
                  <PaginationItem key={page}>
                    <PaginationLink
                      href="#"
                      isActive={page === safeCurrentPage}
                      onClick={event => {
                        event.preventDefault();
                        setCurrentPage(page);
                      }}
                    >
                      {page}
                    </PaginationLink>
                  </PaginationItem>
                ))}
                <PaginationItem>
                  <PaginationNext
                    href="#"
                    onClick={event => {
                      event.preventDefault();
                      if (safeCurrentPage < totalPages) setCurrentPage(safeCurrentPage + 1);
                    }}
                    className={safeCurrentPage === totalPages ? "pointer-events-none opacity-50" : ""}
                  />
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          </div>
        </div>
      )}

      <Dialog open={selectedReceiving !== null} onOpenChange={open => { if (!open) setSelectedReceiving(null); }}>
        <DialogContent className="max-w-3xl">
          {selectedReceiving && (
            <>
              <DialogHeader>
                <DialogTitle>Detalhes do recebimento</DialogTitle>
              </DialogHeader>

              <div className="grid gap-4 md:grid-cols-2">
                <div className="rounded-xl border p-4">
                  <div className="text-xs font-medium uppercase text-muted-foreground">Fornecedor</div>
                  <div className="mt-1 text-sm font-medium">{selectedReceiving.supplierName}</div>
                </div>
                <div className="rounded-xl border p-4">
                  <div className="text-xs font-medium uppercase text-muted-foreground">Data do recebimento</div>
                  <div className="mt-1 text-sm font-medium">{formatDate(selectedReceiving.receivedAt)}</div>
                </div>
                <div className="rounded-xl border p-4">
                  <div className="flex items-start justify-between gap-2">
                    <div className="text-xs font-medium uppercase text-muted-foreground">Nota fiscal</div>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="-mr-2 -mt-1 h-7 gap-1 px-2 text-xs"
                      onClick={openInvoiceModal}
                    >
                      {selectedReceiving.invoiceNumber ? (
                        <><Pencil className="h-3 w-3" />Alterar</>
                      ) : (
                        <><Receipt className="h-3 w-3" />Anexar NF</>
                      )}
                    </Button>
                  </div>
                  <div className="mt-1 text-sm font-medium">
                    {selectedReceiving.invoiceNumber || (
                      <span className="text-warning">Sem NF</span>
                    )}
                  </div>
                  {selectedReceiving.invoiceChangedAt && (
                    <div className="mt-2 border-t pt-2 text-xs text-muted-foreground">
                      <div>
                        Alterada por <span className="font-medium">{selectedReceiving.invoiceChangedByName || selectedReceiving.invoiceChangedByEmail}</span> em {formatDate(selectedReceiving.invoiceChangedAt)}
                      </div>
                      {selectedReceiving.invoiceChangeReason && (
                        <div className="mt-1 italic">"{selectedReceiving.invoiceChangeReason}"</div>
                      )}
                    </div>
                  )}
                </div>
                <div className="rounded-xl border p-4">
                  <div className="text-xs font-medium uppercase text-muted-foreground">Registrado por</div>
                  <div className="mt-1 text-sm font-medium">
                    {selectedReceiving.registeredByName || selectedReceiving.registeredByEmail}
                  </div>
                </div>
                {selectedReceiving.deliveryPersonName && (
                  <div className="rounded-xl border p-4">
                    <div className="text-xs font-medium uppercase text-muted-foreground">Entregador</div>
                    <div className="mt-1 text-sm font-medium">{selectedReceiving.deliveryPersonName}</div>
                    {selectedReceiving.deliveryPersonDocument && (
                      <div className="text-xs text-muted-foreground">{selectedReceiving.deliveryPersonDocument}</div>
                    )}
                  </div>
                )}
                {selectedReceiving.signatureUrl && (
                  <div className="rounded-xl border p-4">
                    <div className="text-xs font-medium uppercase text-muted-foreground">Assinatura</div>
                    <img
                      src={selectedReceiving.signatureUrl}
                      alt="Assinatura do entregador"
                      className="mt-2 max-h-24 bg-white"
                    />
                  </div>
                )}
              </div>

              <div className="overflow-hidden rounded-xl border">
                <div className="border-b px-4 py-3">
                  <div className="text-sm font-medium">Itens da entrada</div>
                </div>
                <div className="max-h-[40vh] overflow-y-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Produto</TableHead>
                        <TableHead>Unidade</TableHead>
                        <TableHead className="text-right">Quantidade</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {selectedReceiving.items.map(item => (
                        <TableRow key={`${selectedReceiving.id}-${item.productId}-${item.unit}`}>
                          <TableCell className="font-medium">{item.productName}</TableCell>
                          <TableCell>{item.unit}</TableCell>
                          <TableCell className="text-right tabular-nums">{formatQty(item.quantity)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>

              {selectedReceiving.receiptPdfUrl && (
                <DialogFooter className="gap-2">
                  <Button
                    variant="outline"
                    className="gap-2"
                    onClick={() => printReceipt(selectedReceiving.receiptPdfUrl!)}
                  >
                    <Printer className="h-4 w-4" />
                    Imprimir
                  </Button>
                  <a
                    href={selectedReceiving.receiptPdfUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    download
                  >
                    <Button className="gap-2">
                      <Download className="h-4 w-4" />
                      Baixar comprovante
                    </Button>
                  </a>
                </DialogFooter>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={invoiceModalOpen} onOpenChange={open => { if (!open) setInvoiceModalOpen(false); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {selectedReceiving?.invoiceNumber ? "Alterar NF" : "Anexar NF"}
            </DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-3">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="invoice-modal-number">Número da NF *</Label>
              <Input
                id="invoice-modal-number"
                placeholder="NF-12345"
                value={invoiceDraft.number}
                onChange={event => setInvoiceDraft(current => ({ ...current, number: event.target.value }))}
                disabled={savingInvoice}
                autoFocus
              />
            </div>
            {selectedReceiving?.invoiceNumber && (
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="invoice-modal-reason">Justificativa *</Label>
                <Input
                  id="invoice-modal-reason"
                  placeholder="Por que a NF está sendo alterada?"
                  value={invoiceDraft.reason}
                  onChange={event => setInvoiceDraft(current => ({ ...current, reason: event.target.value }))}
                  disabled={savingInvoice}
                />
                <p className="text-xs text-muted-foreground">
                  Obrigatória ao substituir uma NF já registrada — fica no histórico do recebimento.
                </p>
              </div>
            )}
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setInvoiceModalOpen(false)} disabled={savingInvoice}>
              Cancelar
            </Button>
            <Button onClick={() => void submitInvoice()} disabled={savingInvoice}>
              {savingInvoice ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              Salvar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={formOpen} onOpenChange={open => { if (!open) closeForm(); }}>
        <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{isEditing ? "Editar recebimento" : "Novo recebimento"}</DialogTitle>
          </DialogHeader>

          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-1.5">
              <Label>Fornecedor *</Label>
              <Select value={draft.supplierId} onValueChange={value => setDraft(current => ({ ...current, supplierId: value }))}>
                <SelectTrigger className="bg-white">
                  <SelectValue placeholder="Selecionar fornecedor" />
                </SelectTrigger>
                <SelectContent>
                  {suppliers.map(supplier => (
                    <SelectItem key={supplier.id} value={supplier.id}>{supplier.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex flex-col gap-4 rounded-xl border p-4">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <Label>Itens do recebimento *</Label>
                  <p className="text-xs text-muted-foreground">Adicione os produtos, unidades e quantidades recebidos.</p>
                </div>
                <Button type="button" variant="outline" size="sm" className="gap-2" onClick={addItem}>
                  <Plus className="h-4 w-4" />
                  Adicionar item
                </Button>
              </div>

              {draft.items.map((item, index) => {
                const availableUnits = itemUnits(item.productId);
                return (
                  <div key={index} className="grid grid-cols-[minmax(0,2.2fr)_140px_140px_auto] items-end gap-3">
                    <div className="flex flex-col gap-1.5">
                      <Label>Produto *</Label>
                      <Select value={item.productId} onValueChange={value => updateItem(index, { productId: value })}>
                        <SelectTrigger className="bg-white">
                          <SelectValue placeholder="Selecionar produto" />
                        </SelectTrigger>
                        <SelectContent>
                          {products.map(product => (
                            <SelectItem key={product.id} value={product.id}>{product.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="flex flex-col gap-1.5">
                      <Label>Unidade *</Label>
                      <Select value={item.unit} onValueChange={value => updateItem(index, { unit: value })}>
                        <SelectTrigger className="bg-white">
                          <SelectValue placeholder="Unidade" />
                        </SelectTrigger>
                        <SelectContent>
                          {availableUnits.map(unit => (
                            <SelectItem key={unit} value={unit}>{unit}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="flex flex-col gap-1.5">
                      <Label>Quantidade *</Label>
                      <Input
                        className="bg-white"
                        type="number"
                        min="0.001"
                        step="0.001"
                        value={item.quantity}
                        onChange={event => updateItem(index, { quantity: event.target.value })}
                        placeholder="0,000"
                      />
                    </div>

                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      disabled={draft.items.length === 1}
                      onClick={() => removeItem(index)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                );
              })}
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div className="flex flex-col gap-1.5">
                <Label>Data e hora do recebimento *</Label>
                <Input
                  className="bg-white"
                  type="datetime-local"
                  value={draft.receivedAt}
                  onChange={event => setDraft(current => ({ ...current, receivedAt: event.target.value }))}
                />
              </div>

              <div className="flex flex-col gap-1.5">
                <Label htmlFor="invoice-number">Número da NF</Label>
                <Input
                  id="invoice-number"
                  className="bg-white"
                  value={draft.invoiceNumber}
                  onChange={event => setDraft(current => ({ ...current, invoiceNumber: event.target.value }))}
                  placeholder="Deixe em branco para recebimento sem NF"
                />
              </div>
            </div>

            <div className={`flex flex-col gap-4 rounded-xl border p-4 ${signatureRequired ? "border-amber-300 bg-amber-50/50" : ""}`}>
              <div className="flex items-center justify-between gap-3">
                <div>
                  <Label className={signatureRequired ? "text-amber-900" : ""}>
                    Assinatura do entregador {signatureRequired ? "*" : "(opcional)"}
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    {signatureRequired
                      ? "Recebimento sem NF exige nome, documento e assinatura do entregador."
                      : "Coletar comprovante assinado mesmo com NF presente."}
                  </p>
                </div>
                {!signatureRequired && (
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={draft.collectSignature}
                      onCheckedChange={(value) => setDraft(current => ({ ...current, collectSignature: value }))}
                    />
                    <span className="text-xs text-muted-foreground">Coletar assinatura</span>
                  </div>
                )}
              </div>

              {showSignatureBlock && (
                <>
                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="flex flex-col gap-1.5">
                      <Label>Nome do entregador {signatureRequired ? "*" : ""}</Label>
                      <Input
                        className="bg-white"
                        value={draft.deliveryPersonName}
                        onChange={event => setDraft(current => ({ ...current, deliveryPersonName: event.target.value }))}
                        placeholder="Nome completo"
                      />
                    </div>
                    <div className="flex flex-col gap-1.5">
                      <Label>Documento {signatureRequired ? "*" : ""}</Label>
                      <Input
                        className="bg-white"
                        value={draft.deliveryPersonDocument}
                        onChange={event => setDraft(current => ({ ...current, deliveryPersonDocument: event.target.value }))}
                        placeholder="RG, CPF ou CNH"
                      />
                    </div>
                  </div>

                  <div className="flex flex-col gap-2">
                    <Label>Assinatura {signatureRequired ? "*" : ""}</Label>
                    <div className="overflow-hidden rounded-lg border bg-white">
                      <SignatureCanvas
                        ref={signaturePadRef}
                        penColor="black"
                        canvasProps={{
                          className: "h-40 w-full touch-none",
                        }}
                      />
                    </div>
                    <Button type="button" variant="outline" size="sm" className="self-start gap-2" onClick={clearSignature}>
                      <Eraser className="h-4 w-4" />
                      Limpar assinatura
                    </Button>
                  </div>
                </>
              )}
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={closeForm} disabled={saving}>Cancelar</Button>
            <Button onClick={() => void handleSave()} disabled={saving}>
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {isEditing ? "Salvar alterações" : "Registrar recebimento"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={deleteTarget !== null} onOpenChange={open => { if (!open && !deleting) setDeleteTarget(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-destructive">Excluir recebimento?</AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <span className="block">
                Esta ação remove o recebimento{deleteTarget?.invoiceNumber ? ` (NF ${deleteTarget.invoiceNumber})` : ""} de{" "}
                <span className="font-medium">{deleteTarget?.supplierName}</span> e reverte a entrada no estoque.
              </span>
              <span className="block">Não é possível desfazer.</span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={event => { event.preventDefault(); void handleDelete(); }}
              disabled={deleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
